import _defineProperty from "@babel/runtime/helpers/defineProperty";
var _ElementUtils;
import { Rectangle } from "abweb/utils/geometry/Rectangle";
import { LogUtils } from "abweb/utils/log/LogUtils";
export class ElementUtils {
  constructor() {
    // static class, can't create an instance
  }

  /**
   * checks to see if the element is in the DOM (attached), not unattached.
   * Literally checks element against the DOM to see if it is attached by walking parent chain.
   * @returns {boolean} true if the element is in the DOM, false otherwise.
   */
  static isElementInDom(element) {
    // note: an older forum post somewhere indicated this was the fastest way to do this test.
    // seems reasonable, it is order N where N is depth of DOM tree.
    // note that even detached DOM elements have an ownerDocument, so we can't use that.
    // This is just a simple walk up the parent chain, looking to see if the containing node is under the DOM document.
    let node = element;
    while (node != null) {
      if (node === document) {
        return true;
      }
      node = node.parentNode;
    }
    return false;
  }

  /**
   * Check if the CSS display property to be "none"
   *
   * @param htmlElement - the HTML element
   *
   * @return if htmlElement is visible or not
   */
  static isCssDisplayVisible(htmlElement) {
    return getComputedStyle(htmlElement).display !== "none";
  }

  /**
   * Set the HTML element display property
   *
   * Sets the display CSS display property. If the newValue is a boolean then
   * the attribute is simply set or unset, otherwise if a string value then
   * will set to the indicated value.
   *
   * Most usage treat this attribute as either visible or not but attribute
   * is more expressive so allow both behaviors.
   *
   * @param htmlElement - the HTML element
   * @param newValue    - if string, new value for CSS display.  If false, "none".
   */
  static setCssDisplay(htmlElement, newValue) {
    let value = ElementUtils.CSS_DISPLAY_UNSET;
    if (typeof newValue === "boolean") {
      // if visible false then will set to NONE which will hide
      if (!newValue) {
        value = ElementUtils.CSS_DISPLAY_NONE;
      }
    } else if (typeof newValue === "string") {
      value = newValue;
    }
    htmlElement.style.display = value;
  }

  /**
   * Return element instance with given id within the given context.
   * Will throw an error if the cssSelector does not match exactly one Element.
   * It will detect when there are multiple elements with the given id value within the given context.
   */
  static getElementById(id, context) {
    // Do NOT use '#id' syntax as will NOT detect multiple unique ids in context.  See getElement test for details.
    return ElementUtils.getElement("[id=" + id + "]", context);
  }
  static getHtmlElementById(id, context) {
    // syntactic sugar to avoid caller having to cast
    // Do NOT use '#id' syntax as will NOT detect multiple unique ids in context.  See getElement test for details.
    return ElementUtils.getHtmlElement("[id=" + id + "]", context);
  }

  /**
   * Return element instance for given cssSelector within the given context.
   * Will throw an error if the cssSelector does not match exactly one Element.
   * <p>
   *     If your cssSelector is simply an id (e.g., '#myId'), please use getElementById('myId') instead as
   * this function will NOT detect when the id is non-unique within the page (browser typically returns *first*
   * id that matches).
   */
  static getElement(cssSelector, context) {
    const nodeList = context.querySelectorAll(cssSelector);
    if (nodeList.length === 1) {
      return nodeList[0];
    }
    const errMsg = "Expected single element to match cssSelector '" + cssSelector + "' but found " + nodeList.length + " elements.";
    // mproteau: I've removed this error log because we're already throwing the error. Callers that catch the error
    // and deal with the lack of an element would otherwise suffer a console log filling with bogus errors. The fact is,
    // getElement should not throw an error. It should simply not return an Element. JavaScript doesn't throw. JQuery
    // doesn't throw. We throw.
    // ElementUtils.LOG.error(errMsg);  // logs stack trace as well
    throw new Error(errMsg);
  }
  static getHtmlElement(cssSelector, context) {
    // syntactic sugar to avoid caller having to cast
    return ElementUtils.getElement(cssSelector, context);
  }
  static getBoundingRect(element) {
    // IE will throw an 'unspecified error' if the HTML element has not been added to the DOM.
    // According to following PR information, other browsers typically return a rectangle of 0 in
    // this case.
    //
    // https://connect.microsoft.com/IE/feedback/details/829392/
    //    calling-getboundingclientrect-on-an-html-element-that-has-not-been-added-to-the-dom-causes-unspecified-error
    let elementBoundingRect;
    try {
      elementBoundingRect = Rectangle.from(element.getBoundingClientRect());
    } catch (e) {
      elementBoundingRect = new Rectangle(0, 0, 0, 0);
    }
    // Note that this once attempted to remove from the width/height the size of the scroll bar by recomputing the
    // right/bottom as the left/top + clientWidth/clientHeight. Unfortunately, getBoundingClientRect takes into account
    // CSS transforms (which is important - things like the AbGraphViewCmp use scaling this way) but clientWidth and
    // clientHeight do not.
    return elementBoundingRect;
  }
  /**
   * Uses canvas.measureText to compute and return the width of the given text of given font in pixels.
   *
   * @param {String} text The text to be rendered.
   * @param {String} className The css class that text is to be rendered with
   * @param {String} fontReference The css font descriptor that text is to be rendered with (e.g. "bold 14px verdana").
   *
   * @see https://stackoverflow.com/questions/118241/calculate-text-width-with-javascript/21015393#21015393
   * @deprecated in favor of ElementUtils.calculateTextWidth
   */
  static getTextWidth(text, className, fontReference) {
    const canvas = document.createElement("canvas");

    // Removed:  adding a class name to the canvas element does nothing - it does not apply the
    //           font characteristics of the named class to the canvas.
    // if (className)
    // {
    //   canvas.className = className;
    // }

    let context = canvas.getContext("2d");
    if (!context) {
      throw new Error("ElementUtils.getTextWidth: no context");
    }
    if (fontReference) {
      if (typeof fontReference === "string") {
        context.font = fontReference;
      } else
        //fontReference is an HTML Element
        {
          context.font = window.getComputedStyle(fontReference).font;
        }
    }
    const retVal = context.measureText(text).width;
    canvas.remove();
    return retVal;
  }
  static setEnabled(element, enabled) {
    element.disabled = !enabled;
    ElementUtils.setEnabledCSS(element, enabled);
  }
  static setEnabledCSS(element, enabled) {
    if (!enabled) {
      element.classList.add("ab-disabled");
    } else {
      element.classList.remove("ab-disabled");
    }
  }
  static getAttributeNames(element) {
    let attributeNames = [];
    const attrs = element.attributes;
    for (let index = 0; index < attrs.length; ++index) {
      attributeNames.push(attrs[index].nodeName);
    }
    attributeNames.sort();
    return attributeNames;
  }

  /**
   * Tests two elements to see that they are identical.
   * The typical usage would be for tests to validate that a HTML DOM element matches some expected element that was
   * explicitly created.
   * TODO this doesn't handle event listeners.
   * TODO this doesn't ignore the id property, which might be an interesting feature to add.
   * @param {Element} element1
   * @param {Element} element2
   * @returns {boolean}
   */
  static compareElements(element1, element2) {
    if (element1.tagName !== element2.tagName) {
      return false;
    }

    // Compare attributes without order sensitivity
    let element1Attributes = ElementUtils.getAttributeNames(element1);
    let element2Attributes = ElementUtils.getAttributeNames(element2);
    if (element1Attributes.join(",") !== element2Attributes.join(",")) {
      return false;
    }

    // ...and values
    // unless you want to compare DOM0 event handlers
    // (onclick="...")

    let name;
    for (let index = 0; index < element1Attributes.length; ++index) {
      name = element1Attributes[index];
      if (element1.getAttribute(name) !== element2.getAttribute(name)) {
        return false;
      }
    }

    // Walk the children
    let element1Child = element1.firstChild;
    let element2Child = element2.firstChild;
    while (element1Child && element2Child) {
      if (element1Child.nodeType !== element2Child.nodeType) {
        return false;
      }
      if (element1Child.nodeType === /*element*/1) {
        if (!ElementUtils.compareElements(element1Child, element2Child)) {
          return false;
        }
      } else if (element1Child.nodeValue !== element2Child.nodeValue) {
        return false;
      }
      element1Child = element1Child.nextSibling;
      element2Child = element2Child.nextSibling;
    }
    if (element1Child || element2Child) {
      return false;
    }
    return true;
  }

  /**
   * Get the value of a CSS variable starting from an optional element.
   * @param {string} varName - name of the css variable to resolve
   * @param {string} defaultValue - if value not found, use this.  If not provided and value not found, returns the empty string.
   * @param {Element} el - place to look for CSS value.  If not provided, uses the document level :root CSS
   * @returns {string}
   */
  static getCss(varName, defaultValue, el) {
    if (el == null) {
      el = document.documentElement;
    }
    const retVal = getComputedStyle(el).getPropertyValue(varName).trim();
    return retVal === "" && defaultValue != null ? defaultValue : retVal;
  }
  static getCssAsNumber(varName, defaultValue, el) {
    const stringVal = ElementUtils.getCss(varName, defaultValue != null ? defaultValue.toString() : undefined, el);
    let numberVal = parseFloat(stringVal);

    //
    // There are some CSS properties whose values are not resolved such as --ab-default-grid-cell-height.
    // If the stringVal contains "calc" then attempt to add it to dom so that calculated value is resolved
    // to something (even if the context is not quite correct).
    //
    // Reference:
    // - https://github.com/w3c/csswg-drafts/issues/2358
    //
    if (stringVal && stringVal.indexOf("calc") > -1) {
      // In order to evaluate the expression, assign it to a numeric style attribute!
      // The body element and min-width are chosen as body element should always exist and min-width
      // is an innocuous CSS property.
      const element = document.getElementsByTagName("body")[0];
      const style = "min-width";

      // Get the current value so can restore later
      const saved = getComputedStyle(element).getPropertyValue(style);

      // Set the property value to new value so it will get evaluated
      element.style.minWidth = stringVal;

      // Get the evaluated value
      const evaluatedValue = getComputedStyle(element).getPropertyValue(style);

      // Restore the value
      element.style.minWidth = saved;
      numberVal = parseFloat(evaluatedValue);
    }
    return isNaN(numberVal) && defaultValue != null ? defaultValue : numberVal;
  }
  static setCss() {
    throw new Error("not implemented yet");
  }
  static isAncestorOf(element, ancestor) {
    if (element === ancestor) {
      return true;
    }
    let parent = element.parentElement;
    while (parent !== null) {
      if (parent === ancestor) {
        return true;
      }
      parent = parent.parentElement;
    }
    return false;
  }

  /**
   * @deprecated Use Array.from(nodeList)
   */
  static nodeListToArray(nodeList) {
    const ret = [];
    nodeList.forEach(node => ret.push(node));
    return ret;
  }
  static createElementFromHTML(html) {
    const doc = new DOMParser().parseFromString(html.trim(), "text/html");
    let el = doc.body.firstElementChild || doc.head.firstElementChild;
    el.remove(); // detach from doc
    return el;
  }
  static offset(el) {
    const rect = el.getBoundingClientRect();
    return {
      top: rect.top + document.body.scrollTop,
      left: rect.left + document.body.scrollLeft
    };
  }
  static empty(el) {
    while (el.firstChild) {
      el.removeChild(el.firstChild);
    }
  }

  /**
   * See if a Dom element, or any of its children, is wider than its containing box.
   * The value is to see if any text is truncated.
   * @param el The element to check
   */
  static isElementTruncated(el) {
    if (el != null) {
      let tw = document.createTreeWalker(el, NodeFilter.SHOW_ELEMENT);
      while (true) {
        let htmlEl = tw.nextNode();
        if (htmlEl == null) {
          return false;
        }
        if (htmlEl.scrollWidth !== htmlEl.offsetWidth && htmlEl.scrollWidth !== 0) {
          return true;
        }
      }
    }
    return false;
  }
}
_ElementUtils = ElementUtils;
// @ts-ignore: noUnusedLocals
_defineProperty(ElementUtils, "LOG", LogUtils.getLogger(__filename));
_defineProperty(ElementUtils, "CSS_DISPLAY_UNSET", "");
_defineProperty(ElementUtils, "CSS_DISPLAY_NONE", "none");
_defineProperty(ElementUtils, "textWidthCanvas", document.createElement("canvas"));
_defineProperty(ElementUtils, "textWidthContext", _ElementUtils.textWidthCanvas.getContext("2d"));
_defineProperty(ElementUtils, "calculateTextWidth", (s, fontReference) => {
  if (_ElementUtils.textWidthContext === null) {
    throw new Error("text width context must not be null");
  }
  // TODO does not handle more complex font issues such as font-stretch, font-style, font-variant, letter-spacing, word-spacing, etc.
  if (typeof fontReference === "string") {
    _ElementUtils.textWidthContext.font = fontReference;
  } else {
    const fontWeight = _ElementUtils.getCss("font-weight", undefined, fontReference);
    const fontSize = _ElementUtils.getCss("font-size", undefined, fontReference);
    const fontFamily = _ElementUtils.getCss("font-family", undefined, fontReference);
    _ElementUtils.textWidthContext.font = `${fontWeight} ${fontSize} ${fontFamily}`;
  }
  return _ElementUtils.textWidthContext.measureText(s).width;
});
//# sourceMappingURL=ElementUtils.js.map
